/**
 * Command Center — Data Health widget.
 *
 * Surfaces data-source failover health from the snapshot's `data_health`
 * quadrant (system_monitoring_api.get_data_source_health). Each source carries
 * its OWN healthy/available flags + consecutive-failure count from the failover
 * engine, so the widget colors off those directly — no invented thresholds.
 * Shows the active source, per-source freshness, failover cooldown, and crypto
 * stream state. Read-only; off the execution path.
 */

import React from 'react';
import { Database } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import type { CommandCenterQuadrant } from '../../lib/api';

interface SourceHealth {
  healthy?: boolean;
  available?: boolean;
  last_success?: string | null;
  last_failure?: string | null;
  consecutive_failures?: number;
}

const relTime = (iso?: string | null): string => {
  if (!iso) return 'never';
  try {
    const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 60) return `${secs}s ago`;
    const m = Math.round(secs / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.round(m / 60)}h ago`;
  } catch {
    return '';
  }
};

// healthy+available -> green; available-but-unhealthy (in backoff) -> amber; down -> red.
const sourceColor = (s: SourceHealth): string => {
  if (s.healthy && s.available) return '#1FC78F';
  if (s.available) return '#FFCC00';
  return '#E5484D';
};

const DataHealthWidget: React.FC<{ data: CommandCenterQuadrant | undefined }> = ({ data }) => {
  const errored = !!data && typeof data.error === 'string';
  const q = (data as Record<string, unknown> | undefined) ?? {};
  const innerError = typeof q.error === 'string' ? (q.error as string) : null;
  const activeSource = (q.active_source as string | null) ?? null;
  const rawSources = (q.sources as Record<string, unknown> | undefined) ?? {};
  const sources = Object.entries(rawSources).filter(([k]) => k !== '_failover_meta') as Array<[string, SourceHealth]>;
  const meta = rawSources._failover_meta as { in_cooldown?: boolean } | undefined;
  const freshness = (q.asset_freshness as Record<string, unknown> | undefined) ?? {};
  const cryptoStreaming = q.crypto_streaming as boolean | undefined;

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Database className="h-4 w-4" /> Data Health
        </CardTitle>
        <Badge variant={errored || innerError ? 'destructive' : 'secondary'}>
          {errored || innerError ? 'degraded' : (activeSource || 'live')}
        </Badge>
      </CardHeader>
      <CardContent className="flex-1">
        {errored ? (
          <div className="text-sm text-destructive">{String(data?.error)}</div>
        ) : innerError ? (
          <div className="text-sm text-muted-foreground">Controller offline: {innerError}</div>
        ) : sources.length === 0 ? (
          <div className="text-sm text-muted-foreground">No data sources registered.</div>
        ) : (
          <div className="space-y-1.5">
            {sources.map(([name, s]) => (
              <div key={name} className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: sourceColor(s) }} />
                  {name}
                  {activeSource === name && <span className="text-[10px] font-semibold text-primary">active</span>}
                </span>
                <span className="text-xs text-muted-foreground">
                  {(s.consecutive_failures ?? 0) > 0 ? `${s.consecutive_failures} fail · ` : ''}
                  {relTime(s.last_success)}
                </span>
              </div>
            ))}
            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
              <span>{Object.keys(freshness).length} asset(s) tracked</span>
              {meta?.in_cooldown && <span className="text-amber-500">failover cooldown</span>}
              {cryptoStreaming !== undefined && <span>crypto stream: {cryptoStreaming ? 'on' : 'off'}</span>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DataHealthWidget;
